import React from 'react';

import { useAppContext } from './AppContext';
import { appConfig } from './config';
import { cookieConsents } from './cookieConsents';
import { addScript, environment } from './util';

// export interface UseIubendaInitializerProps {}

declare global {
  interface Window {
    _iub?: {
      cs?: {
        api?: {
          ccpaApplies(): boolean;
          gdprApplies(): boolean;
          lgpdApplies(): boolean;
          openPreferences(): void;
        };
        consent?: {
          purposes?: { 1: boolean; 2: boolean; 3: boolean; 4: boolean; 5: boolean };
        };
      };
      csConfiguration?: {
        callback?: {
          onReady?: (consent: boolean) => void;
          onConsentFirstGiven?: (event?: string) => void;
        };
      };
    };
  }
}

export const useIubendaInitializer = () => {
  const { locale } = useAppContext();

  const handleIubendaOnConsentReady = React.useCallback(() => {
    if (environment === 'LOCAL') {
      console.log('#iubendaOnConsentReady: ', window._iub?.cs?.consent?.purposes);
    }

    if (window._iub?.cs?.consent?.purposes) {
      const purposes = window._iub.cs.consent.purposes;
      cookieConsents.set({
        consents: {
          experience: purposes[3],
          marketing: purposes[5],
          necessary: purposes[1],
          preferences: purposes[2],
          statistics: purposes[4],
        },
        provider: 'Iubenda',
        regulations: {
          ccpa: window._iub.cs.api?.ccpaApplies(),
          gdpr: window._iub.cs.api?.gdprApplies(),
          lgpd: window._iub.cs.api?.lgpdApplies(),
        },
      });
    }
  }, []);

  React.useEffect(() => {
    // Richiede il refresh della pagina dopo la selezione dei cookie da parte dell'utente.
    if (appConfig.iubenda?.useWidget === true && appConfig.iubenda.widgetId) {
      // Configura i callback PRIMA di caricare il widget
      window._iub = window._iub ?? {};
      window._iub.csConfiguration = window._iub.csConfiguration ?? {};
      window._iub.csConfiguration.callback = {
        onReady: () => {
          window.dispatchEvent(new CustomEvent('iubenda_consent'));
        },
        onConsentFirstGiven: () => {
          window.dispatchEvent(new CustomEvent('iubenda_consent'));
        },
      };

      addScript({
        id: 'iubenda-widget',
        src: `https://embeds.iubenda.com/widgets/${appConfig.iubenda.widgetId}.js`,
        type: 'text/javascript',
      });

      window.addEventListener('iubenda_consent', handleIubendaOnConsentReady);

      return () => {
        window.removeEventListener('iubenda_consent', handleIubendaOnConsentReady);
      };
    } else if (appConfig.iubenda && appConfig.iubenda.siteId && locale?.languageCode) {
      console.log('Iubenda initializer');
      addScript({
        id: 'iubenda',
        text: `
          var _iub = _iub || [];
          _iub.csLangConfiguration = ${appConfig.iubenda.languages
            .map((lang) => `{"${lang.languageCode}":{"cookiePolicyId":${lang.cookiePolicyId}}}`)
            .join(',')};
          _iub.csConfiguration = {
            "lang": "${locale.languageCode}",
            "siteId": ${appConfig.iubenda.siteId},
            "cookiePolicyId": ${
              appConfig.iubenda.languages.find((elem) => elem.languageCode === locale.languageCode)?.cookiePolicyId ??
              appConfig.iubenda.languages[0].cookiePolicyId
            },
            "storage":{"useSiteId": ${appConfig.iubenda.useSiteId ?? true}},
            "callback": {
              onReady: () => {
                window.dispatchEvent(new CustomEvent("iubenda_consent"));
              },
              onConsentFirstGiven: () => {
                window.dispatchEvent(new CustomEvent("iubenda_consent"));
              },
            }
          };
          `,
      });
      addScript({
        id: 'iubenda-autoblocking',
        src: `https://cs.iubenda.com/autoblocking/${appConfig.iubenda.siteId}.js`,
        type: 'text/javascript',
      });
      addScript({
        id: 'iubenda-gpp-stub',
        src: 'https://cdn.iubenda.com/cs/gpp/stub.js',
        type: 'text/javascript',
      });
      addScript({
        async: true,
        charset: 'UTF-8',
        id: 'iubenda-cs',
        src: 'https://cdn.iubenda.com/cs/iubenda_cs.js',
        type: 'text/javascript',
      });

      window.addEventListener('iubenda_consent', handleIubendaOnConsentReady);

      return () => {
        window.removeEventListener('iubenda_consent', handleIubendaOnConsentReady);
      };
    }
  }, [handleIubendaOnConsentReady, locale?.languageCode]);
};
